import React, { useContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import OrderProp from './propTypes/orderProp';
import CurrencyProp from '../profile/propTypes/currencyProp';
import { ORDER_STATUSES, compose } from '../../services/utilityFunctions';
import Notifications from '../../services/notifications';
import { useModal } from '../../services/customHooks';
import CreditNotesModal from './shopItems/CreditNotesModal';
import MetaContext from '../../contextProviders/MetaContext';
import ConfigsContext from '../../contextProviders/ConfigsContext';
import DetailHeader from './detail/DetailHeader';
import {
  OPPORTUNITIES_STATUS,
  OPPORTUNITIES_STATUSES,
} from '../opportunities/components/helpers/opportunitiesListHelper';
import OrderWrapper from './detail/OrderWrapper';
import LostReasonModal from './modal/LostReasonModal';

const updateOrder = async (t, _csrf, order, params = {}) => {
  try {
    await axios.put(`/api/market/opportunity/${order?._id}`, params, {
      headers: {
        'X-Gamification-API': 'Games',
        'Accept-Version': '1.0.0',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    });

    const { data: updatedOrder } = await axios.get(
      `/api/market/opportunity/${order?._id}`,
      {
        params: {
          populate: [
            'products.product',
            'products.product.form',
            'products.parentProduct',
            'products.packProduct',
            'owner',
            'seller',
            'operator',
            'history.user',
            'shipping',
          ],
        },
        headers: {
          'X-Gamification-API': 'Games',
          'Accept-Version': '1.0.0',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      },
    );
    Notifications.showNotificationSuccess(
      t('success'),
      t('SUCCESS_UPDATING_OPPORTUNITY'),
    );
    return updatedOrder.data;
  } catch (err) {
    Notifications.showErrorFromRequest(err, '');
    return order;
  }
};

function ShopUserOrderPage({
  t,
  _csrf,
  order,
  currencies,
  isAcademy,
  isOpportunity,
}) {
  const { userPortalMenus } = useContext(MetaContext);
  const { tenant, organization } = useContext(ConfigsContext);
  const { lostReasons = [] } = organization?.settings?.opportunities || {};

  const [isLoadingBtn, setLoadingBtn] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [showLostReasonModal, setShowLostReasonModal] = useModal({
    show: false,
    data: null,
  });
  const [orderState, setOrderState] = useState(order);
  const pageInformation = useMemo(() => {
    const mainTitle =
      userPortalMenus.find(u => u.type === 'marketplace')?.name ||
      t('shop.market_place');
    return {
      title: order.number
        ? `${isOpportunity ? t('opportunity.number') : t('order.number')}: ${
            order.number
          }`
        : mainTitle,
      status: isOpportunity
        ? OPPORTUNITIES_STATUSES
        : ORDER_STATUSES.map(s => ({ ...s, text: `status.${s.text}` })),
    };
  }, []);

  // region Credit Notes Modal
  const [creditNotes, setCreditNotes] = useModal({ show: false, data: null });

  const handleCreditNotes = orderCreditNotes => {
    setCreditNotes({ show: true, data: orderCreditNotes });
  };
  const handleClose = () => {
    setCreditNotes({ show: false, data: null });
  };
  // endregion Credit Notes Modal

  /** region Add Documents by updating the opportunity */
  const onAddDocument = useCallback(
    async params => {
      setLoadingBtn(true);
      const updatedOrder = await updateOrder(t, _csrf, orderState, params);
      setOrderState(updatedOrder);
      setLoadingBtn(false);
    },
    [orderState],
  );
  /** endregion Add Documents by updating the opportunity */
  const handleSaveOrder = useCallback(
    async params => {
      setIsLoadingSave(true);
      const shouldSelectLostReason =
        isOpportunity &&
        params?.status === OPPORTUNITIES_STATUS.REJECTED &&
        lostReasons?.length > 0;

      if (shouldSelectLostReason) {
        setShowLostReasonModal({
          show: true,
          data: params,
        });
        return;
      }

      const updatedOrder = await updateOrder(t, _csrf, orderState, params);
      console.log('*ORDER STATE', updatedOrder);
      setOrderState(updatedOrder);
      setIsLoadingSave(false);
    },
    [orderState],
  );

  const onCloseLostReasonsModal = useCallback(
    async (isToSave, lostReason, status) => {
      if (isToSave) {
        const updatedOrder = await updateOrder(t, _csrf, orderState, {
          status,
          lostReason,
        });
        setOrderState(updatedOrder);
      }
      setShowLostReasonModal({
        show: false,
        data: null,
      });
      setIsLoadingSave(false);
    },
    [setShowLostReasonModal, setIsLoadingSave],
  );

  return (
    <div className="m-content">
      {showLostReasonModal?.show && (
        <LostReasonModal
          isLoading={isLoadingSave}
          onClose={(isToSave, lostReason) =>
            onCloseLostReasonsModal(
              isToSave,
              lostReason,
              showLostReasonModal?.data?.status,
            )
          }
          opportunity={showLostReasonModal.data}
          organization={organization}
        />
      )}
      {creditNotes.show && (
        <CreditNotesModal
          creditNotes={creditNotes.data}
          currency={order.currency}
          onClose={handleClose}
        />
      )}
      <Title title={pageInformation.title}>
        <ContentContainer>
          <SubHeader title={pageInformation.title} />
          <div id="userOrderPage" className="m-content shop">
            <DetailHeader
              status={pageInformation.status}
              order={orderState}
              isOpportunity={isOpportunity}
            />
            <OrderWrapper
              order={orderState}
              isAcademy={isAcademy}
              isOpportunity={isOpportunity}
              currencies={currencies}
              tenant={tenant}
              onCreditNotesClick={handleCreditNotes}
              onAddDocument={onAddDocument}
              isLoadingBtn={isLoadingBtn}
              isLoading={isLoadingSave}
              onSaveOrder={handleSaveOrder}
            />
          </div>
        </ContentContainer>
      </Title>
    </div>
  );
}

ShopUserOrderPage.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  order: PropTypes.shape(OrderProp.propType).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  isAcademy: PropTypes.bool,
  isOpportunity: PropTypes.bool,
};

ShopUserOrderPage.defaultProps = {
  isAcademy: false,
  isOpportunity: false,
};

export default compose(translate, withCSRF)(ShopUserOrderPage);
